import React, { useEffect } from 'react';

import { makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';

import { Typography } from '../../../components/atoms';
import { ControlGroup, RadioGroup } from '../../../components/layouts';
import { analyticsService } from '../../../core/services';
import * as Api from '../../../weborder/Api';
import { OptionInput } from '../components';
import { useMenuOptionsUiState } from '../utils';

interface Props {
  currency: Api.Currency;
  languageCode: string;
  optionSet: Api.MenuItemOptionSet;
  translate: any;
}

const useStyles = makeStyles((theme: Theme) => ({
  optionImage: {
    boxSizing: 'content-box',
    marginRight: theme.spacing(1.5),
    objectFit: 'cover',
  },

  optionLabel: {
    alignItems: 'center',
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'space-between',

    '& .fd-text': {
      margin: 0,
      padding: 0,
    },
  },
  optionLabelDescription: {
    alignItems: 'center',
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'flex-start',

    '& .fd-text': {
      fontSize: theme.typography.pxToRem(16),
    },
  },
  optionLabelImage: {
    height: 65,
    width: 65,
  },

  optionsList: {
    margin: theme.spacing(0, 3),
    padding: 0,
  },
  optionListItem: {
    padding: theme.spacing(1.25, 0),
  },

  optionPrice: {
    minWidth: theme.spacing(8),
    textAlign: 'right',
  },

  optionsSet: {
    paddingBottom: theme.spacing(2),

    '& label': {
      margin: 0,
      padding: 0,
    },

    '& .fd-text__h2': {
      padding: theme.spacing(2.5, 2, 1.75),
    },
  },

  [theme.breakpoints.up('sm')]: {
    optionsSet: {
      '& .fd-text__h2': {
        fontSize: theme.typography.pxToRem(21),
        padding: theme.spacing(2.5, 3, 1.75),
      },
    },
  },
}));

export function OptionSet(props: Props): JSX.Element {
  const { currency, languageCode, optionSet, translate } = props;

  const classes = useStyles();

  const { state, actions } = useMenuOptionsUiState();
  const { optionsDraft, progressBtnEnabled } = state;
  const { goForward, setBtnEnabled, updateDraftOptions } = actions;

  const isSingleChoice =
    optionSet.MinSelectCount === 1 && optionSet.MaxSelectCount === 1;

  useEffect(() => {
    const hasMinimumNeeded = optionsDraft.length >= optionSet.MinSelectCount;

    if (hasMinimumNeeded && !progressBtnEnabled) {
      setBtnEnabled(true);
    }
    if (!hasMinimumNeeded && progressBtnEnabled) {
      setBtnEnabled(false);
    }
  }, [optionsDraft]);

  useEffect(() => {
    if (optionsDraft.length === optionSet.MaxSelectCount) {
      goForward();
    }
  }, [optionsDraft]);

  const trackModifierSelected = (modifier): void => {
    const eventData = {
      amp_hasDescription: false,
      amp_hasImage: !!modifier.ImageUrl,
      amp_hasModifiersOfModifiers: !!modifier.NextMenuItemOptionSetId,
      amp_label: 'Modifier Selected',
      amp_modifierId: modifier.MenuItemOptionSetItemId.toString(),
      amp_modifierName: modifier.Name,
      amp_modifierPrice: modifier.Price,
    };
    analyticsService.track(eventData);
  };

  const trackModifierDeselected = (modifier): void => {
    const eventData = {
      amp_hasImage: !!modifier.ImageUrl,
      amp_hasModifiersOfModifiers: !!modifier.NextMenuItemOptionSetId,
      amp_label: 'Modifier Deselected',
      amp_modifierId: modifier.MenuItemOptionSetItemId.toString(),
      amp_modifierName: modifier.Name,
      amp_modifierPrice: modifier.Price,
    };
    analyticsService.track(eventData);
  };

  const getOptionSetTitle = (option: Api.MenuItemOptionSet): string => {
    const { MinSelectCount, MaxSelectCount, Name } = option;

    const prefix = Name ? `${Name} - ` : '';

    if (MinSelectCount === MaxSelectCount) {
      if (MaxSelectCount === 1) {
        return `${prefix}${translate('Choose_one')}`;
      }

      return `${prefix}${translate('Choose')}${' '}${MaxSelectCount}`;
    }

    if (MinSelectCount === 0 && MaxSelectCount > 0) {
      return `${prefix}${translate('Choose_up_to')}${' '}${MaxSelectCount}`;
    }

    if (MinSelectCount === 0 && MaxSelectCount === -1) {
      return `${prefix}${translate('Choose_any')}`;
    }

    if (MinSelectCount > 0 && MaxSelectCount !== -1) {
      return `${prefix}${translate(
        'Choose',
      )}${' '}${MinSelectCount}${' - '}${MaxSelectCount}`;
    }

    if (MinSelectCount > 0 && MaxSelectCount === -1) {
      return `${prefix}${translate(
        'Choose',
      )}${' '}${MinSelectCount}${' '}${translate('Or_more')}`;
    }

    return translate('Select_an_option');
  };

  const findMenuItemOption = (optionId: number): Api.MenuItemOptionSetItem =>
    optionSet.MenuItemOptionSetItems.find(
      (menuItemOption) => menuItemOption.MenuItemOptionSetItemId === optionId,
    );

  const addMenuItemOption = (
    newOption: Api.MenuItemOptionSetItem,
  ): Api.MenuItemOptionSetItem[] => {
    trackModifierSelected(newOption);
    return [...optionsDraft, newOption];
  };

  const removeMenuItemOption = (
    newOption: Api.MenuItemOptionSetItem,
    optionId: number,
  ): Api.MenuItemOptionSetItem[] => {
    trackModifierDeselected(newOption);
    return optionsDraft.filter(
      (option) => option.MenuItemOptionSetItemId !== optionId,
    );
  };

  const handleCheckboxChange = (event): void => {
    const newOptionId = parseInt(event.target.value);
    const newOption = findMenuItemOption(newOptionId);

    const isOptionAlreadySelected = (
      optionId: number,
    ): Api.MenuItemOptionSetItem | undefined =>
      optionsDraft.find(
        (option) => option.MenuItemOptionSetItemId === optionId,
      );
    const newDraftOptions = isOptionAlreadySelected(newOptionId)
      ? removeMenuItemOption(newOption, newOptionId)
      : addMenuItemOption(newOption);

    updateDraftOptions(newDraftOptions);
  };

  const handleRadioGroupChange = (event): void => {
    const newOptionId = parseInt(event.target.value);
    const newDraftOption = findMenuItemOption(newOptionId);

    trackModifierSelected(newDraftOption);

    updateDraftOptions([newDraftOption]);
  };

  const renderMenuOptionInputs = (
    menuItemOptionSetItems: Api.MenuItemOptionSetItem[],
  ): JSX.Element[] => {
    const isSelected = (m: Api.MenuItemOptionSetItem): boolean =>
      optionsDraft.some(
        (option) =>
          option.MenuItemOptionSetItemId === m.MenuItemOptionSetItemId,
      );

    return menuItemOptionSetItems.map((menuItemOption) => (
      <OptionInput
        currency={currency}
        imageUrl={menuItemOption.ImageUrl}
        isAvailable={menuItemOption.IsAvailable}
        isMasterOptionSet={optionSet.IsMasterOptionSet}
        isSelected={!!isSelected(menuItemOption)}
        isSingleChoice={isSingleChoice}
        key={menuItemOption.MenuItemOptionSetItemId}
        languageCode={languageCode}
        menuItemOptionSetItemId={menuItemOption.MenuItemOptionSetItemId}
        name={menuItemOption.Name}
        onCheckboxChange={handleCheckboxChange}
        price={menuItemOption.Price}
      />
    ));
  };

  const optionSetTitle = getOptionSetTitle(optionSet);
  const optionsSetStyles = clsx(classes.optionsSet, 'fd-section__menu-options');

  return (
    <section aria-label={optionSetTitle} className={optionsSetStyles}>
      <Typography variant="h2">{optionSetTitle}</Typography>

      {isSingleChoice && (
        <RadioGroup onChange={handleRadioGroupChange}>
          <ul className={classes.optionsList}>
            {renderMenuOptionInputs(optionSet.MenuItemOptionSetItems)}
          </ul>
        </RadioGroup>
      )}

      {!isSingleChoice && (
        <ControlGroup>
          <ul className={classes.optionsList}>
            {renderMenuOptionInputs(optionSet.MenuItemOptionSetItems)}
          </ul>
        </ControlGroup>
      )}
    </section>
  );
}
